import React, { useState } from 'react';
import { MarketingNavbar } from '../components/layout/MarketingNavbar';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import {
    ArrowRight, Activity, Brain, Users, LineChart,
    ShieldCheck, HeartPulse, X, CheckCircle, Send,
    Stethoscope, GraduationCap, Building2, Phone, Mail, MapPin
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Landing() {
    const [isDemoModalOpen, setDemoModalOpen] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const handleRequestDemo = (e) => {
        e.preventDefault();
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            setDemoModalOpen(false);
        }, 3000);
    };

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-brand-100 selection:text-brand-900 overflow-x-hidden">
            <MarketingNavbar />

            {/* Hero Section */}
            <section className="relative pt-24 pb-32 lg:pt-32 lg:pb-48 overflow-hidden">
                {/* Premium Background Elements */}
                <div className="absolute inset-0 bg-mesh-gradient opacity-60"></div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-50/50 via-white to-white pointer-events-none"></div>

                <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-6xl">
                    <div className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50/80 backdrop-blur-sm px-4 py-1.5 text-xs sm:text-sm font-semibold text-brand-700 mb-8 shadow-sm">
                        <span className="flex h-2 w-2 rounded-full bg-brand-600 mr-2 animate-pulse"></span>
                        Advanced Clinical Intelligence
                    </div>

                    <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-8 leading-[1.1] font-outfit uppercase">
                        Precision Care with <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-400">P2G LIFE</span>
                    </h1>

                    <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
                        The next-generation clinical assessment platform for developmental screening, therapeutic analysis, and institutional care orchestration.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-20">
                        <Link to="/register" className="w-full sm:w-auto">
                            <Button size="lg" className="w-full sm:w-auto h-16 px-10 text-lg group shadow-premium rounded-2xl bg-brand-600 hover:bg-brand-700 transition-all font-outfit font-bold uppercase tracking-widest">
                                Clinical Registration
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                        <Link to="/register" className="w-full sm:w-auto" state={{ role: 'Patient' }}>
                            <Button
                                variant="outline"
                                size="lg"
                                className="w-full sm:w-auto h-16 px-10 text-lg bg-white/80 backdrop-blur-md shadow-soft border-brand-200 rounded-2xl hover:bg-brand-50 transition-all font-outfit font-bold text-brand-700 hover:border-brand-300 uppercase tracking-widest"
                            >
                                <HeartPulse className="mr-2 h-6 w-6" />
                                Patient Portal
                            </Button>
                        </Link>
                    </div>

                    <div className="relative mx-auto max-w-5xl">
                        <div className="absolute -inset-1 bg-gradient-to-r from-brand-500/20 to-secondary-500/20 rounded-[2.5rem] blur-2xl opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative p-3 bg-white/40 backdrop-blur-2xl rounded-[2rem] border border-white/50 shadow-2xl">
                            <div className="rounded-[1.5rem] overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/9] flex items-center justify-center group">
                                <img
                                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070"
                                    alt="Melete-Nova Analytics"
                                    className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
                                <div className="relative flex flex-col items-center">
                                    <div className="h-20 w-20 rounded-full bg-white/20 backdrop-blur-xl flex items-center justify-center mb-4 border border-white/30 text-white shadow-2xl">
                                        <Activity className="h-10 w-10 animate-pulse" />
                                    </div>
                                    <p className="text-white font-bold text-xl drop-shadow-md font-outfit uppercase tracking-widest">Real-time Clinical Analytics</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-32 bg-slate-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-center mb-20 max-w-3xl mx-auto">
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-outfit tracking-tight uppercase">Holistic Care Ecosystem</h2>
                        <p className="text-lg text-slate-600 font-light">Expertly designed tools for every stakeholder, ensuring consistency and excellence in assessment and intervention.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                        <FeatureCard
                            icon={<Brain className="h-8 w-8" />}
                            title="Structured Evaluations"
                            description="Domain-wise clinical assessments with evidence-based criteria for precise baseline identification."
                        />
                        <FeatureCard
                            icon={<LineChart className="h-8 w-8" />}
                            title="Growth Tracking"
                            description="Visualize developmental trajectories with multi-dimensional charts and sophisticated trend analysis."
                        />
                        <FeatureCard
                            icon={<Activity className="h-8 w-8" />}
                            title="Intervention Design"
                            description="Automated goal suggestions and adaptive teaching strategies based on real-time performance data."
                        />
                        <FeatureCard
                            icon={<Users className="h-8 w-8" />}
                            title="Secure Collaboration"
                            description="Role-based access for multi-disciplinary teams, fostering seamless communication between stakeholders."
                        />
                        <FeatureCard
                            icon={<ShieldCheck className="h-8 w-8" />}
                            title="Clinical Compliance"
                            description="Adheres to global healthcare security standards, keeping all sensitive medical records fully protected."
                        />
                        <FeatureCard
                            icon={<Building2 className="h-8 w-8" />}
                            title="Institutional Scalability"
                            description="Designed for enterprises, managing multiple centers, staff cohorts, and thousands of patient profiles."
                        />
                    </div>
                </div>
            </section>

            {/* Stakeholder Section */}
            <section id="stakeholders" className="py-32 bg-white">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 font-outfit uppercase tracking-tight">Solutions tailored for every need.</h2>
                            <div className="space-y-8">
                                <StakeholderItem
                                    icon={<Building2 className="text-brand-600" />}
                                    title="For Institutions & Rehab Centers"
                                    description="Centralize data, automate reporting, and standardize care quality across your entire organization."
                                />
                                <StakeholderItem
                                    icon={<Users className="text-brand-600" />}
                                    title="For Caregivers & Patients"
                                    description="Stay informed with real-time progress updates and gain clear insights into home-based support strategies."
                                    link="/register"
                                />
                                <StakeholderItem
                                    icon={<Stethoscope className="text-brand-600" />}
                                    title="For Health Professionals"
                                    description="Spend less time on paperwork and more on patient care with our intuitive evaluation and tracking tools."
                                />
                            </div>
                        </div>
                        <div className="relative">
                            <div className="absolute -inset-10 bg-brand-100 rounded-full blur-3xl opacity-30 animate-pulse"></div>
                            <img
                                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=2070"
                                alt="Melete-Nova Clinical Excellence"
                                className="relative rounded-3xl shadow-2xl border border-slate-100"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-32 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-600 via-secondary-500 to-accent-500"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-5 gap-16">
                            <div className="lg:col-span-2">
                                <h2 className="text-4xl font-bold font-outfit mb-6 uppercase tracking-tight">Let's connect.</h2>
                                <p className="text-slate-400 text-lg mb-10 leading-relaxed">
                                    Whether you're an institution looking to scale or a caregiver seeking consistency, our team is ready to support your journey.
                                </p>

                                <div className="space-y-6">
                                    <div className="flex items-center gap-4 group cursor-pointer">
                                        <div className="h-12 w-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-brand-600 transition-colors">
                                            <Mail className="h-6 w-6 text-brand-400 group-hover:text-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500 font-semibold mb-0.5 uppercase tracking-widest">Contact Email</p>
                                            <p className="text-lg text-white font-medium">Smilesfoundationindia@gmail.com</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 group cursor-pointer">
                                        <div className="h-12 w-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-brand-600 transition-colors">
                                            <Building2 className="h-6 w-6 text-brand-400 group-hover:text-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500 font-semibold mb-0.5 uppercase tracking-widest">Office Headquarters</p>
                                            <p className="text-lg text-white font-medium">Plot 13-9-123, PR NAGAR X ROADS, opposite Neelima hospitals, Erragadda, Hyderabad 500018</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="lg:col-span-3">
                                <Card className="bg-white text-slate-900 border-0 shadow-2xl overflow-hidden rounded-[2rem]">
                                    <CardHeader className="p-8 pb-4">
                                        <CardTitle className="text-2xl font-outfit uppercase tracking-tight">Consultation Request</CardTitle>
                                        <CardDescription className="text-slate-500 font-medium">Provide your details and we'll reach out within 24 hours.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-8">
                                        <form className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Full Name</label>
                                                    <input className="w-full bg-slate-50 border-slate-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition-all" placeholder="John Carter" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Email Address</label>
                                                    <input type="email" className="w-full bg-slate-50 border-slate-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition-all" placeholder="john@example.com" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Phone Number</label>
                                                    <input className="w-full bg-slate-50 border-slate-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition-all" placeholder="+1 (555) 000-0000" />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Organization</label>
                                                    <input className="w-full bg-slate-50 border-slate-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition-all" placeholder="Institute Name" />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Clinical Interest</label>
                                                <textarea rows={4} className="w-full bg-slate-50 border-slate-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-brand-500 outline-none transition-all" placeholder="Describe your institutional or individual needs..."></textarea>
                                            </div>
                                            <Button className="w-full h-14 text-sm font-black uppercase tracking-widest shadow-premium rounded-xl bg-brand-600 hover:bg-brand-700 mt-4">
                                                Request A Free Consultation
                                            </Button>
                                        </form>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-50 py-24 border-t border-slate-200">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
                        <div className="col-span-1 lg:col-span-1">
                            <Link to="/" className="flex items-center gap-3 mb-6">
                                <div className="h-8 w-8 rounded-lg bg-brand-600 flex items-center justify-center text-white shadow-premium">
                                    <HeartPulse className="h-5 w-5" />
                                </div>
                                <span className="text-2xl font-black tracking-tight text-slate-900 font-outfit uppercase">P2G Life</span>
                            </Link>
                            <p className="text-slate-500 mb-8 max-w-xs leading-relaxed text-sm font-medium">
                                Empowering clinical excellence through data-driven assessment and compassionate intervention planning.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-slate-900 font-black mb-6 font-outfit uppercase tracking-widest text-xs">Platform</h4>
                            <ul className="space-y-4 text-slate-500 text-xs font-black uppercase tracking-[0.2em]">
                                <li><a href="#features" className="hover:text-brand-600 transition-colors">Features</a></li>
                                <li><a href="#stakeholders" className="hover:text-brand-600 transition-colors">Solutions</a></li>
                                <li><Link to="/register" className="hover:text-brand-600 transition-colors">Register</Link></li>
                                <li><Link to="/login" className="hover:text-brand-600 transition-colors">Login</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-slate-900 font-black mb-6 font-outfit uppercase tracking-widest text-xs">Support</h4>
                            <ul className="space-y-4 text-slate-500 text-xs font-black uppercase tracking-[0.2em]">
                                <li><a href="#" className="hover:text-brand-600 transition-colors">Help Center</a></li>
                                <li><a href="#" className="hover:text-brand-600 transition-colors">Clinical Guides</a></li>
                                <li><a href="#" className="hover:text-brand-600 transition-colors">Privacy Policy</a></li>
                                <li><a href="#" className="hover:text-brand-600 transition-colors">Terms of Service</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-slate-900 font-black mb-6 font-outfit uppercase tracking-widest text-xs">Professional</h4>
                            <div className="space-y-4">
                                <p className="text-slate-900 font-black text-[10px] uppercase tracking-widest">Lead Clinician</p>
                                <p className="text-xs font-bold text-slate-600 leading-tight">Prof.(Dr.) MALLIKARJUNA RAO DUBISETTY</p>
                                <div className="flex items-center gap-3 text-slate-500 text-xs font-medium">
                                    <Mail className="h-4 w-4 text-brand-600" />
                                    <span>Smilesfoundationindia@gmail.com</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
                        <p>© 2026 P2G Life. All rights reserved.</p>
                        <div className="flex gap-8">
                            <a href="#" className="hover:text-brand-600 transition-colors">Linkedin</a>
                            <a href="#" className="hover:text-brand-600 transition-colors">Twitter</a>
                            <a href="#" className="hover:text-brand-600 transition-colors">Instagram</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, description }) {
    return (
        <Card className="border-0 bg-white shadow-soft hover:shadow-premium hover:-translate-y-2 transition-all duration-500 p-8 rounded-[2rem] group">
            <div className="h-16 w-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-8 text-brand-600 group-hover:bg-brand-600 group-hover:text-white transition-all duration-500 shadow-sm border border-brand-100/50">
                {icon}
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-4 font-outfit uppercase tracking-tight">{title}</h3>
            <p className="text-slate-500 leading-relaxed font-light text-sm">{description}</p>
        </Card>
    );
}

function StakeholderItem({ icon, title, description, link }) {
    return (
        <div className="flex gap-6 items-start p-6 rounded-[2rem] hover:bg-slate-50 transition-all group">
            <div className="h-12 w-12 rounded-xl bg-brand-50 shrink-0 flex items-center justify-center shadow-sm border border-brand-100 group-hover:bg-brand-600 group-hover:text-white transition-all">
                {icon}
            </div>
            <div>
                <h4 className="text-lg font-black text-slate-900 mb-2 font-outfit uppercase tracking-tight">{title}</h4>
                <p className="text-slate-500 leading-relaxed text-sm mb-3 font-medium">{description}</p>
                {link && (
                    <Link to={link} className="text-xs font-black text-brand-600 hover:text-brand-700 flex items-center gap-2 group uppercase tracking-widest">
                        Portal Registration
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                )}
            </div>
        </div>
    );
}
